import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { usePatientReflectionOverviewConnected } from "@grow-therapy-team/seedling-components";
import {
  DrawerBody,
  DrawerHeader,
  IconButton,
  Text,
} from "@growtherapy/sprout-ui";
import { useTranslate } from "@growtherapy/i18n";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTrackEvent } from "../../segment/segment";
import { EventTypeEnum } from "../../segment/types";
import { roomAtom } from "../../video-sdk";
import { useHandleReflectionTopicCompletionCallback } from "./useHandleReflectionTopicCompletionCallback";

export function ReflectionTopicsDrawer({
  title,
  content,
  onClickBackButton,
}: {
  title: string;
  content: React.ReactNode;
  onClickBackButton?: () => void;
}) {
  const t = useTranslate();
  return (
    <>
      <DrawerHeader>
        <div className="flex items-center">
          {onClickBackButton && (
            <IconButton
              onClick={onClickBackButton}
              className="mr-4 w-8 h-8 flex justify-center items-center"
              iconSize="xs"
              aria-label={t("telehealth-core:core.goBack")}
              data-testid="reflection-topics.button.back"
              iconDefinition={faArrowLeft}
            />
          )}
          <Text as="h2" variant="lg" className="fs-exclude">
            {title}
          </Text>
        </div>
      </DrawerHeader>
      {/* NOTE: Padding is set by the content itself, as certain views want to take up the full interior of the drawer. */}
      <DrawerBody className="!p-0">{content}</DrawerBody>
    </>
  );
}

export function ReflectionTopicsDrawerWrapper() {
  const { patientShortId } = useParams();
  const isInSession = !!useAtomValue(roomAtom);
  const { userViewed } = useTrackEvent();
  const handleReflectionTopicCompletion =
    useHandleReflectionTopicCompletionCallback();

  const {
    title,
    content,
    goToPreviousPatientReflectionView,
    hasPreviousPatientReflectionView,
  } = usePatientReflectionOverviewConnected({
    patientShortId: patientShortId ?? "",
    onUpdateTopicCompletionStatusSuccess: handleReflectionTopicCompletion,
  });

  const onClickBackButton = hasPreviousPatientReflectionView
    ? goToPreviousPatientReflectionView
    : undefined;

  useEffect(() => {
    userViewed(EventTypeEnum.REFLECTION_TOPICS_VIEWED, {
      patientShortId: patientShortId ?? "",
      inSession: isInSession,
    });
  }, [patientShortId, userViewed, isInSession]);

  return (
    <ReflectionTopicsDrawer
      title={title ?? ""}
      content={content}
      onClickBackButton={onClickBackButton}
    />
  );
}
